import api, { ApiResponse, PaginatedApiResponse } from './axiosInstance';

export interface UserReference {
  _id: string;
  name: string;
  email: string;
}

// Customer interface matching backend
export interface Customer {
  _id: string;
  name: string;
  code: string;
  companyName?: string;
  customerType: 'LLP' | 'Pvt Ltd' | 'Proprietor' | 'Public Ltd' | 'Partnership' | 'Sole Proprietorship' | 'Other';
  email: string;
  phone: string;
  mobile?: string;
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  billingAddress?: {
    street: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  contacts?: Array<{
    name: string | null;
    designation: string | null;
    email: string | null;
    phone: string | null;
    mobile?: string | null;
    isPrimary: boolean;
  }>;
  gstNumber?: string;
  panNumber?: string;
  registrationNumber?: string;
  website?: string;
  industry?: string;
  status: 'active' | 'inactive';
  notes?: string;
  tags?: string[];
  createdBy: string | UserReference;
  updatedBy: string | UserReference;
  createdAt: string;
  updatedAt: string;
}

// Create customer data interface (camelCase to match backend)
export interface CreateCustomerData {
  name: string;
  code: string;
  companyName?: string;
  customerType: 'LLP' | 'Pvt Ltd' | 'Proprietor' | 'Public Ltd' | 'Partnership' | 'Sole Proprietorship' | 'Other';
  email: string;
  phone?: string;
  mobile: string;
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  billingAddress?: {
    street: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  contacts?: Array<{
    name: string | null;
    designation: string | null;
    email: string | null;
    phone: string | null;
    mobile?: string | null;
    isPrimary: boolean;
  }>;
  gstNumber?: string;
  panNumber?: string;
  registrationNumber?: string;
  website?: string;
  industry?: string;
  notes?: string;
  tags?: string[];
}

// Update customer data interface
export interface UpdateCustomerData extends Partial<CreateCustomerData> { }

// Query parameters interface
export interface CustomerQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  name?: string;
  code?: string;
  'customer-type'?: 'LLP' | 'Pvt Ltd' | 'Proprietor' | 'Public Ltd' | 'Partnership' | 'Sole Proprietorship' | 'Other';
  status?: 'active' | 'inactive';
  industry?: string;
  city?: string;
  state?: string;
  country?: string;
  'sort-by'?: string;
  'sort-order'?: 'asc' | 'desc';
}

class CustomerService {
  private baseUrl = '/customers';

  private normalizeCustomer = (raw: any): Customer => {
    const createdBy =
      raw && typeof raw.createdBy === 'object' && raw.createdBy !== null
        ? {
            _id: raw.createdBy.id ?? raw.createdBy._id ?? '',
            name: raw.createdBy.name ?? '',
            email: raw.createdBy.email ?? ''
          }
        : raw?.createdBy ?? '';
    const updatedBy =
      raw && typeof raw.updatedBy === 'object' && raw.updatedBy !== null
        ? {
            _id: raw.updatedBy.id ?? raw.updatedBy._id ?? '',
            name: raw.updatedBy.name ?? '',
            email: raw.updatedBy.email ?? ''
          }
        : raw?.updatedBy ?? '';

    return {
      _id: raw?._id ?? raw?.id ?? '',
      name: raw?.name ?? '',
      code: raw?.code ?? '',
      companyName: raw?.companyName,
      customerType: raw?.customerType,
      email: raw?.email ?? '',
      phone: raw?.phone ?? '',
      mobile: raw?.mobile,
      address: raw?.address,
      billingAddress: raw?.billingAddress,
      contacts: raw?.contacts,
      gstNumber: raw?.gstNumber,
      panNumber: raw?.panNumber,
      registrationNumber: raw?.registrationNumber,
      website: raw?.website,
      industry: raw?.industry,
      status: raw?.status,
      notes: raw?.notes,
      tags: raw?.tags,
      createdBy,
      updatedBy,
      createdAt: raw?.createdAt ?? '',
      updatedAt: raw?.updatedAt ?? '',
    } as Customer;
  };

  /**
   * Get all customers with pagination and filtering
   * If limit and page are not provided, returns all customers without pagination
   */
  async getCustomers(params?: CustomerQueryParams): Promise<PaginatedApiResponse<Customer> | { data: Customer[] }> {
    const response = await api.get<PaginatedApiResponse<any> | { success: boolean; message: string; data: any[] }>(this.baseUrl, { params });
    
    // Check if response has meta (paginated) or just data (non-paginated)
    if ('meta' in response.data) {
      // Paginated response
      const normalized: PaginatedApiResponse<Customer> = {
        ...response.data,
        data: (response.data?.data ?? []).map((c: any) => this.normalizeCustomer(c)),
      };
      return normalized;
    } else {
      // Non-paginated response (all customers)
      return {
        data: (response.data?.data ?? []).map((c: any) => this.normalizeCustomer(c)),
      };
    }
  }

  /**
   * Get a single customer by ID
   */
  async getCustomerById(id: string): Promise<ApiResponse<Customer>> {
    const response = await api.get<ApiResponse<any>>(`${this.baseUrl}/${id}`);
    const normalized: ApiResponse<Customer> = {
      ...response.data,
      data: this.normalizeCustomer(response.data?.data),
    };
    return normalized;
  }

  /**
   * Create a new customer
   */
  async createCustomer(customerData: CreateCustomerData): Promise<ApiResponse<Customer>> {
    const response = await api.post<ApiResponse<any>>(this.baseUrl, customerData);
    const normalized: ApiResponse<Customer> = {
      ...response.data,
      data: this.normalizeCustomer(response.data?.data),
    };
    return normalized;
  }

  /**
   * Update a customer
   */
  async updateCustomer(id: string, customerData: UpdateCustomerData): Promise<ApiResponse<Customer>> {
    const response = await api.put<ApiResponse<any>>(`${this.baseUrl}/${id}`, customerData);
    const normalized: ApiResponse<Customer> = {
      ...response.data,
      data: this.normalizeCustomer(response.data?.data),
    };
    return normalized;
  }

  /**
   * Inactivate (soft delete) a customer
   */
  async inactivateCustomer(id: string): Promise<ApiResponse<Customer>> {
    try {
      const response = await api.patch<ApiResponse<any>>(`${this.baseUrl}/${id}/inactivate`);
      const normalized: ApiResponse<Customer> = {
        ...response.data,
        data: this.normalizeCustomer(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error inactivating customer:', error);
      throw new Error(error.message || 'Failed to inactivate customer');
    }
  }

  /**
   * Activate a customer
   */
  async activateCustomer(id: string): Promise<ApiResponse<Customer>> {
    try {
      const response = await api.patch<ApiResponse<any>>(`${this.baseUrl}/${id}/activate`);
      const normalized: ApiResponse<Customer> = {
        ...response.data,
        data: this.normalizeCustomer(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error activating customer:', error);
      throw new Error(error.message || 'Failed to activate customer');
    }
  }

  /**
   * Search customers
   */
  async searchCustomers(searchTerm: string, params?: Omit<CustomerQueryParams, 'search'>): Promise<PaginatedApiResponse<Customer>> {
    try {
      const searchParams = { ...params, search: searchTerm };
      const result = await this.getCustomers(searchParams);
      // Ensure we always return paginated response
      if ('meta' in result) {
        return result;
      }
      // If non-paginated, convert to paginated format
      return {
        success: true,
        message: 'Customers fetched successfully',
        data: result.data,
        meta: {
          page: params?.page || 1,
          limit: params?.limit || result.data.length,
          totalPages: 1,
          totalDocs: result.data.length,
          hasPrevPage: false,
          hasNextPage: false,
          prevPage: null,
          nextPage: null,
        },
      };
    } catch (error: any) {
      console.error('Error searching customers:', error);
      throw new Error(error.message || 'Failed to search customers');
    }
  }

  /**
   * Get customers by type
   */
  async getCustomersByType(type: 'LLP' | 'Pvt Ltd' | 'Proprietor' | 'Public Ltd' | 'Partnership' | 'Sole Proprietorship' | 'Other', params?: Omit<CustomerQueryParams, 'customer-type'>): Promise<PaginatedApiResponse<Customer>> {
    try {
      const searchParams = { ...params, 'customer-type': type };
      const result = await this.getCustomers(searchParams);
      // Ensure we always return paginated response
      if ('meta' in result) {
        return result;
      }
      // If non-paginated, convert to paginated format
      return {
        success: true,
        message: 'Customers fetched successfully',
        data: result.data,
        meta: {
          page: params?.page || 1,
          limit: params?.limit || result.data.length,
          totalPages: 1,
          totalDocs: result.data.length,
          hasPrevPage: false,
          hasNextPage: false,
          prevPage: null,
          nextPage: null,
        },
      };
    } catch (error: any) {
      console.error('Error fetching customers by type:', error);
      throw new Error(error.message || 'Failed to fetch customers by type');
    }
  }

  /**
   * Get active customers only
   */
  async getActiveCustomers(params?: Omit<CustomerQueryParams, 'status'>): Promise<PaginatedApiResponse<Customer>> {
    try {
      const searchParams = { ...params, status: 'active' as const };
      const result = await this.getCustomers(searchParams);
      // Ensure we always return paginated response
      if ('meta' in result) {
        return result;
      }
      // If non-paginated, convert to paginated format
      return {
        success: true,
        message: 'Customers fetched successfully',
        data: result.data,
        meta: {
          page: params?.page || 1,
          limit: params?.limit || result.data.length,
          totalPages: 1,
          totalDocs: result.data.length,
          hasPrevPage: false,
          hasNextPage: false,
          prevPage: null,
          nextPage: null,
        },
      };
    } catch (error: any) {
      console.error('Error fetching active customers:', error);
      throw new Error(error.message || 'Failed to fetch active customers');
    }
  }

  /**
   * Delete a customer
   */
  async deleteCustomer(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete<ApiResponse<any>>(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting customer:', error);
      throw new Error(error.message || 'Failed to delete customer');
    }
  }
}

export const customerService = new CustomerService();
export default customerService;